import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import {
  type CreatePatient,
  useCreatePatient,
  useSearchPatients,
} from "@elation/api-client/src/stitch-web";
import { SearchPatientsSortBy } from "@elation/api-client/src/stitch-web/models/searchPatientsSortBy";

import {
  type CreatePatientSubmitValues,
  type PatientSearchFormValues,
  PatientsPage,
} from "@src/deprecated/pages/Patients";
import { navigation } from "@src/shared/navigation";
import { useActor } from "@src/shared/providers/actorProvider";
import { getHttpStatus } from "@src/shared/utils/http/errors";
import { NaiveDate } from "@src/shared/utils/types";

function errorMessage(err: unknown): string | null {
  const data = (err as { response?: { data?: unknown } } | null)?.response
    ?.data;

  if (data && typeof data === "object" && "message" in data) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }

  if (err instanceof Error && err.message.trim()) return err.message;

  return null;
}

function logError(
  errorId: string,
  err: unknown,
  context?: Record<string, unknown>,
) {
  const error =
    err instanceof Error
      ? { name: err.name, message: err.message }
      : { type: typeof err };

  console.error(`[${errorId}]`, { errorId, error, ...context });
}

export function meta() {
  return [
    { title: "Patients" },
    { name: "description", content: "Patients page" },
  ];
}

type FilterState = {
  q?: string;
  dob?: string;
  zip?: string;
};

type SortState = {
  sortBy: SearchPatientsSortBy;
  order: "asc" | "desc";
};

const DEFAULT_SORT: SortState = { sortBy: "lastName", order: "asc" };

const PAGE_SIZE = 10;

function formValuesToFilters(values: PatientSearchFormValues): FilterState {
  const dob = values.date_of_birth
    ? NaiveDate.toString(values.date_of_birth)
    : undefined;
  return {
    q: values.q?.trim() || undefined,
    dob,
    zip: values.zip_code || undefined,
  };
}

export default function Patients() {
  const navigate = useNavigate();
  const actor = useActor();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<FilterState>({});
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  // Stack of cursors visited. Top of stack = cursor for the CURRENT page.
  // Empty stack = first page. Advancing pushes the response's nextCursor.
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const currentCursor = cursorStack[cursorStack.length - 1];

  const {
    data: patientsData,
    isLoading,
    isError,
    error,
  } = useSearchPatients(
    { ...filters, ...sort, limit: PAGE_SIZE, cursor: currentCursor },
    {
      query: {
        // 4xx responses are caller errors (bad cursor, bad filter); retrying
        // just replays the same bad request. Only retry on 5xx/network.
        retry: (failureCount, err) => {
          const status = getHttpStatus(err);
          if (status != null && status >= 400 && status < 500) return false;
          return failureCount < 2;
        },
      },
    },
  );

  const createPatient = useCreatePatient({
    mutation: {
      onSuccess: async (result) => {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "/gw/stitch-web/api/patients",
        });

        const patientId = result.data?.patient?.id;

        if (patientId == null) {
          logError(
            "patients.create_patient.missing_patient_id",
            new Error("Create patient response did not include a patient id"),
            {
              hasData: Boolean(result.data),
              hasPatient: Boolean(result.data?.patient),
            },
          );
          toast.success("Patient created");
          return;
        }

        const patientUrl = navigation.patientDetail(String(patientId));

        toast.success("Patient created", {
          action: {
            label: "View patient",
            onClick: () => {
              navigate(patientUrl, {
                state: { source: "patient list" },
              });
            },
          },
        });
      },
    },
  });

  const patients = patientsData?.data?.items ?? [];
  const searchErrors = patientsData?.data?.errors ?? {};
  const nextCursor = patientsData?.data?.nextCursor ?? null;

  // Auto-recover from a stale cursor (e.g. row deleted, tampered URL). The
  // backend signals this with HTTP 400 and we silently reset to the first
  // page; any other error is surfaced to the user by PatientsPage.
  const isStaleCursor =
    isError && currentCursor != null && getHttpStatus(error) === 400;

  // A cursor is only meaningful under the filter + sort it was minted for
  // (the server binds cursors to sortBy and would 400 on mismatch). Reset
  // the stack on any filter or sort change so Prev doesn't walk stale pages.
  const handleSearch = (values: PatientSearchFormValues) => {
    setFilters(formValuesToFilters(values));
    setCursorStack([]);
  };

  const handleSortChange = (
    sortBy: SearchPatientsSortBy,
    order: "asc" | "desc",
  ) => {
    setSort({ sortBy, order });
    setCursorStack([]);
  };

  const handleNextPage = () => {
    if (nextCursor) setCursorStack((stack) => [...stack, nextCursor]);
  };

  const handlePrevPage = () => {
    setCursorStack((stack) => stack.slice(0, -1));
  };

  const handleCreatePatient = async (values: CreatePatientSubmitValues) => {
    if (actor.type !== "physician") {
      const err = new Error("Only physicians can create patients.");
      logError("patients.create_patient.non_physician_actor", err, {
        actorType: actor.type,
      });
      throw err;
    }

    const data = {
      ...values,
      primaryCarePhysicianId: actor.user.id,
    } satisfies CreatePatient;

    try {
      await createPatient.mutateAsync({ data });
    } catch (err) {
      logError("patients.create_patient.mutation_failed", err);
      throw err;
    }
  };

  useEffect(() => {
    if (isStaleCursor) setCursorStack([]);
  }, [isStaleCursor]);

  return (
    <PatientsPage
      patients={patients}
      searchErrors={searchErrors}
      isLoading={isLoading}
      isError={isError && !isStaleCursor}
      onSearch={handleSearch}
      sortBy={sort.sortBy}
      order={sort.order}
      onSortChange={handleSortChange}
      hasNextPage={Boolean(nextCursor)}
      hasPrevPage={cursorStack.length > 0}
      onNextPage={handleNextPage}
      onPrevPage={handlePrevPage}
      canCreatePatient={actor.type === "physician"}
      isCreatingPatient={createPatient.isPending}
      createPatientError={
        createPatient.isError
          ? (errorMessage(createPatient.error) ??
            "Unable to create patient. Please try again.")
          : null
      }
      onCreatePatientDialogOpenChange={() => createPatient.reset()}
      onCreatePatient={handleCreatePatient}
    />
  );
}
